import { ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { useDebounce } from './useDebounce';

/** Mirrors use-debounce's `useDebounce(value, delay)` — a debounced snapshot of `source`. */
export function useDebouncedValue<T>(
  source: MaybeRefOrGetter<T>,
  delay = 300,
  onChange?: (value: T) => void
) {
  const debouncedValue = ref(toValue(source)) as Ref<T>;

  const { trigger, cancel, flush, isPending } = useDebounce((value: T) => {
    debouncedValue.value = value;
    onChange?.(value);
  }, delay);

  watch(
    () => toValue(source),
    value => {
      trigger(value);
    },
    { flush: 'sync' }
  );

  return { debouncedValue, cancel, flush, isPending };
}
